import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { toast } from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"

import type { BaselineDeviationsBySimulationRequest } from "@/types_api/backend/transport/baseline_deviations_by_simulation_request"
import type { BaselineDeviationBySimulation } from "@/types_api/backend/transport/baseline_deviations_by_simulation_response"
import type { BaselineHistoryRequest } from "@/types_api/backend/transport/baseline_history_request"
import type { BaselineHistoryPoint } from "@/types_api/backend/transport/baseline_history_response"
import type { CreateBaselineRequest } from "@/types_api/backend/transport/create_baseline_request"
import type { GetBaselineResponse } from "@/types_api/backend/transport/get_baseline_response"
import type { ListBaselinesResponse } from "@/types_api/backend/transport/list_baselines_response"
import type { UpdateBaselineRequest } from "@/types_api/backend/transport/update_baseline_request"

import type { BaselineResponse } from "../types"

interface UseBaselinesQueryOptions<TData> {
  enabled?: boolean
  gcTime?: number
  staleTime?: number
  select?: (data: BaselineResponse[]) => TData
}

interface UseBaselineDetailQueryOptions {
  enabled?: boolean
  gcTime?: number
  staleTime?: number
}

interface UseBaselineHistoryQueryOptions {
  enabled?: boolean
  gcTime?: number
  staleTime?: number
}

interface UseBaselineDeviationsQueryOptions {
  enabled?: boolean
}

interface BaselineHistoryInput {
  baselineId: string
  bucketInterval?: BaselineHistoryRequest["bucket_interval"]
  endDate?: string
  startDate?: string
  timezone?: string
}

interface BaselineDeviationsInput {
  baselineId: string
  simulationOutputIds: string[]
}

/**
 * The two lifecycle states a baseline can be toggled between from the UI.
 * Distinct from the transport `BaselineStatus`, which also includes "ARCHIVED".
 */
export type BaselineToggleStatus = "ACTIVE" | "STANDBY"
type ToggleVariables = UpdateBaselineRequest & { status: BaselineToggleStatus }

interface ToggleContext {
  displacedActiveUlid: string | undefined
  previousDetail: GetBaselineResponse | undefined
  previousList: ListBaselinesResponse | undefined
}

function findDisplacedActiveBaselineUlid(
  previousList: ListBaselinesResponse | undefined,
  variables: ToggleVariables,
): string | undefined {
  if (variables.status !== "ACTIVE" || !previousList) {
    return undefined
  }

  const target = previousList.baselines.find(
    (baseline) => baseline.ulid === variables.ulid,
  )
  if (!target) {
    return undefined
  }

  return previousList.baselines.find(
    (baseline) =>
      baseline.ulid !== variables.ulid &&
      baseline.status === "ACTIVE" &&
      baseline.metric_id === target.metric_id &&
      baseline.agent_id === target.agent_id &&
      baseline.test_set_id === target.test_set_id &&
      baseline.persona_id === target.persona_id,
  )?.ulid
}

function updateListForStatus(
  previousList: ListBaselinesResponse | undefined,
  variables: ToggleVariables,
): ListBaselinesResponse | undefined {
  if (!previousList) {
    return previousList
  }

  const target = previousList.baselines.find(
    (baseline) => baseline.ulid === variables.ulid,
  )
  if (!target) {
    return previousList
  }

  return {
    ...previousList,
    baselines: previousList.baselines.map((baseline) => {
      if (baseline.ulid === variables.ulid) {
        return { ...baseline, status: variables.status }
      }
      if (
        variables.status === "ACTIVE" &&
        baseline.status === "ACTIVE" &&
        baseline.metric_id === target.metric_id &&
        baseline.agent_id === target.agent_id &&
        baseline.test_set_id === target.test_set_id &&
        baseline.persona_id === target.persona_id
      ) {
        return { ...baseline, status: "STANDBY" as const }
      }
      return baseline
    }),
  }
}

export function useBaselinesList<TData = BaselineResponse[]>(
  metricId: string | undefined,
  options?: UseBaselinesQueryOptions<TData>,
) {
  const request = useMemo(() => ({ metric_id: metricId ?? "" }), [metricId])
  const enabled = (options?.enabled ?? true) && Boolean(metricId)

  return useTransportQuery({
    operation: "listBaselines",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      gcTime: options?.gcTime,
      staleTime: options?.staleTime,
      enabled,
      select: (response) => {
        const data = response.baselines
        return options?.select ? options.select(data) : (data as TData)
      },
    },
  })
}

export function useCreateBaselineTransport(metricId: string) {
  const transportQueryUtils = useTransportQueryUtils()
  const createMutation = useTransportMutation("createBaseline")

  const createBaseline = useCallback(
    async (request: Omit<CreateBaselineRequest, "metric_id">) => {
      const created = await createMutation.mutateAsync({
        ...request,
        metric_id: metricId,
      })
      await transportQueryUtils.invalidate("listBaselines", {
        metric_id: metricId,
      })
      return created
    },
    [createMutation, metricId, transportQueryUtils],
  )

  return {
    createBaseline,
    isPending: createMutation.isPending,
  }
}

export function useBaselineDetailActions(metricId: string, ulid: string) {
  const transportQueryUtils = useTransportQueryUtils()
  const updateMutation = useTransportMutation("updateBaseline")
  const archiveMutation = useTransportMutation("archiveBaseline")

  const invalidate = useCallback(async () => {
    await Promise.all([
      transportQueryUtils.invalidate("listBaselines", { metric_id: metricId }),
      transportQueryUtils.invalidate("getBaseline", {
        metric_id: metricId,
        ulid,
      }),
    ])
  }, [metricId, transportQueryUtils, ulid])

  const updateBaseline = useCallback(
    async (request: Omit<UpdateBaselineRequest, "metric_id" | "ulid">) => {
      await updateMutation.mutateAsync({
        ...request,
        metric_id: metricId,
        ulid,
      })
      await invalidate()
    },
    [invalidate, metricId, ulid, updateMutation],
  )

  const archiveBaseline = useCallback(async () => {
    await archiveMutation.mutateAsync({ metric_id: metricId, ulid })
    await invalidate()
  }, [archiveMutation, invalidate, metricId, ulid])

  return {
    archiveBaseline,
    isArchiving: archiveMutation.isPending,
    isUpdating: updateMutation.isPending,
    updateBaseline,
  }
}

export function useBaselineStatusToggle(metricId: string) {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()

  const mutation = useTransportMutation("updateBaseline", {
    onMutate: async (variables) => {
      const next = variables.status
      if (next !== "ACTIVE" && next !== "STANDBY") {
        return undefined
      }

      const toggleVariables = {
        ...variables,
        status: next,
      } satisfies ToggleVariables
      const listRequest = { metric_id: metricId }
      const detailRequest = {
        metric_id: metricId,
        ulid: variables.ulid,
      }

      await Promise.all([
        queryClient.cancelQueries({
          queryKey: transportQueryUtils.getKey("listBaselines", listRequest),
        }),
        queryClient.cancelQueries({
          queryKey: transportQueryUtils.getKey("getBaseline", detailRequest),
        }),
      ])

      const previousList = transportQueryUtils.getQueryData(
        "listBaselines",
        listRequest,
      )
      const previousDetail = transportQueryUtils.getQueryData(
        "getBaseline",
        detailRequest,
      )
      const displacedActiveUlid = findDisplacedActiveBaselineUlid(
        previousList,
        toggleVariables,
      )
      const nextList = updateListForStatus(previousList, toggleVariables)

      if (nextList) {
        transportQueryUtils.setQueryData("listBaselines", nextList, listRequest)
      }

      if (previousDetail) {
        transportQueryUtils.setQueryData(
          "getBaseline",
          { ...previousDetail, status: next },
          detailRequest,
        )
      }

      return {
        displacedActiveUlid,
        previousList,
        previousDetail,
      } satisfies ToggleContext
    },
    onError: (error, variables, context) => {
      const listRequest = { metric_id: metricId }
      const detailRequest = {
        metric_id: metricId,
        ulid: variables.ulid,
      }

      if (context?.previousList !== undefined) {
        transportQueryUtils.setQueryData(
          "listBaselines",
          context.previousList,
          listRequest,
        )
      }
      if (context?.previousDetail !== undefined) {
        transportQueryUtils.setQueryData(
          "getBaseline",
          context.previousDetail,
          detailRequest,
        )
      }

      toast.error({
        title: "Failed to update target",
        error,
        fallback: "Could not update the target.",
      })
    },
    onSettled: (_data, _error, variables, context) => {
      const invalidations = [
        transportQueryUtils.invalidate("listBaselines", {
          metric_id: metricId,
        }),
        transportQueryUtils.invalidate("getBaseline", {
          metric_id: metricId,
          ulid: variables.ulid,
        }),
      ]

      if (context?.displacedActiveUlid) {
        invalidations.push(
          transportQueryUtils.invalidate("getBaseline", {
            metric_id: metricId,
            ulid: context.displacedActiveUlid,
          }),
        )
      }

      void Promise.all(invalidations)
    },
  })

  const toggleStatus = useCallback(
    (ulid: string, status: BaselineToggleStatus) => {
      mutation.mutate({ metric_id: metricId, ulid, status })
    },
    [mutation, metricId],
  )

  return { toggleStatus, isPending: mutation.isPending }
}

export function useBaselineDetail(
  metricId: string | undefined,
  ulid: string | undefined,
  options?: UseBaselineDetailQueryOptions,
) {
  const request = useMemo(
    () => ({ metric_id: metricId ?? "", ulid: ulid ?? "" }),
    [metricId, ulid],
  )
  const enabled = (options?.enabled ?? true) && Boolean(metricId && ulid)

  return useTransportQuery({
    operation: "getBaseline",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      gcTime: options?.gcTime,
      staleTime: options?.staleTime,
      enabled,
    },
  })
}

export function useBaselineHistory(
  input: BaselineHistoryInput,
  options?: UseBaselineHistoryQueryOptions,
) {
  const request = useMemo<BaselineHistoryRequest>(
    () => ({
      baseline_id: input.baselineId,
      bucket_interval: input.bucketInterval,
      end_date: input.endDate,
      start_date: input.startDate,
      timezone: input.timezone,
    }),
    [
      input.baselineId,
      input.bucketInterval,
      input.endDate,
      input.startDate,
      input.timezone,
    ],
  )
  const enabled = (options?.enabled ?? true) && Boolean(input.baselineId)

  return useTransportQuery({
    operation: "getBaselineHistory",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      gcTime: options?.gcTime,
      staleTime: options?.staleTime,
      enabled,
      select: (response): BaselineHistoryPoint[] => response.history,
    },
  })
}

export function useBaselineDeviationsBySimulation(
  input: BaselineDeviationsInput,
  options?: UseBaselineDeviationsQueryOptions,
) {
  const request = useMemo<BaselineDeviationsBySimulationRequest>(
    () => ({
      baseline_id: input.baselineId,
      simulation_output_ids: input.simulationOutputIds,
    }),
    [input.baselineId, input.simulationOutputIds],
  )
  const enabled =
    (options?.enabled ?? true) &&
    Boolean(input.baselineId) &&
    input.simulationOutputIds.length > 0

  return useTransportQuery({
    operation: "getBaselineDeviationsBySimulation",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      select: (response): BaselineDeviationBySimulation[] =>
        response.deviations,
    },
  })
}
